import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  useMediaQuery,
} from "@mui/material";
import { Chip } from "@/shared/components/Chip";
import type { AmbiguousCandidate } from "../utils/geofenceError";

interface Props {
  open: boolean;
  candidates: AmbiguousCandidate[];
  title?: string;
  message?: string;
  onCancel: () => void;
  onConfirm: (locationId: string) => void;
  busy?: boolean;
}

export function LocationPickerDialog({
  open,
  candidates,
  title,
  message,
  onCancel,
  onConfirm,
  busy,
}: Props) {
  const [chosen, setChosen] = useState<string>("");
  const fullScreen = useMediaQuery("(max-width: 480px)");

  useEffect(() => {
    if (open) {
      setChosen(candidates[0]?.locationId ?? "");
    }
  }, [open, candidates]);

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      slotProps={{
        paper: {
          sx: {
            borderRadius: fullScreen ? 0 : 2,
            maxHeight: fullScreen ? "100vh" : "min(560px, calc(100vh - 32px))",
          },
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 15, fontWeight: 600, py: 1.5, px: 2 }}>
        {title ?? "Pick a location"}
      </DialogTitle>
      <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
        <p
          style={{
            fontSize: 12,
            lineHeight: 1.45,
            color: "var(--ink-3)",
            margin: "0 0 12px",
          }}
        >
          {message ??
            "Choose which assigned location to record this attendance against."}
        </p>
        <div role="radiogroup" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {candidates.map((c) => {
            const selected = chosen === c.locationId;
            return (
              <button
                key={c.locationId}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setChosen(c.locationId)}
                disabled={busy}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: `1px solid ${selected ? "var(--accent, #2563eb)" : "var(--line)"}`,
                  background: selected ? "rgba(37, 99, 235, 0.06)" : "var(--surface)",
                  cursor: busy ? "not-allowed" : "pointer",
                  textAlign: "left",
                  width: "100%",
                  minWidth: 0,
                  transition: "border-color 120ms, background 120ms",
                }}
              >
                <Radio
                  size="small"
                  checked={selected}
                  tabIndex={-1}
                  sx={{ p: 0, mt: "2px", pointerEvents: "none" }}
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 6,
                      minWidth: 0,
                    }}
                  >
                    <strong
                      style={{
                        fontSize: 13,
                        lineHeight: 1.3,
                        wordBreak: "break-word",
                      }}
                    >
                      {c.name}
                    </strong>
                    {c.isPrimary ? <Chip tone="info">Primary</Chip> : null}
                    {c.inside === true ? (
                      <Chip tone="success">Inside</Chip>
                    ) : c.inside === false ? (
                      <Chip tone="danger">Outside</Chip>
                    ) : null}
                  </div>
                  <span
                    style={{
                      fontSize: 11.5,
                      color: "var(--ink-3)",
                      lineHeight: 1.35,
                    }}
                  >
                    {c.distanceM.toLocaleString()} m from center · radius {c.radiusM} m
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
      <DialogActions
        sx={{
          px: 2,
          py: 1.25,
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}
      >
        <Button size="small" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={busy || !chosen}
          onClick={() => onConfirm(chosen)}
        >
          {busy ? "Submitting…" : "Use this location"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
